import { useState, useEffect, useMemo } from 'react';
import uniqueId from 'lodash/uniqueId';
import PropTypes from 'prop-types';

// material-ui
import { useTheme } from '@mui/material/styles';
import { FormControl, FormHelperText, InputLabel, OutlinedInput, Select, Button, Autocomplete } from '@mui/material';

// ===========================|| FIREBASE - CustomComponent ||=========================== //

/**
 * Group digits with comma
 *
 * @param {number} nStr
 */
const addCommas = function (nStr) {
    nStr += '';
    // Round float number
    var nDot = nStr.indexOf('.');
    var nLen = nStr.length - 1 - nDot;
    if (nDot > 0 && nLen > 0) {
        nStr = parseFloat(parseFloat(nStr).toFixed(nLen <= 4 ? nLen : 4)).toString();
    }
    // Add commoas
    return nStr.toString().replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ',');
};

/**
 * Remove comma from digits
 *
 * @param {string or event} val
 */
const toFloat = function (val) {
    val += '';
    return toEnDigit(val?.toString()).replace(/[^0-9.]/g, '');
};

/**
 * To english digits
 *
 * @param {string or event} val
 */
const toEnDigit = (val) => {
    val += '';
    return val.toString().replace(/[٠-٩۰-۹]/g, (n) => 15 & n.charCodeAt(0));
};

const CustomInput = ({ type, name, label, value, commaInput, error, labelStyle, onChange, ...others }) => {
    const theme = useTheme();
    const [id] = useState(uniqueId(`outlined-${name}-`));
    const [val, setVal] = useState(value);

    useEffect(() => {
        setValue;
    }, [value]);

    const setValue = useMemo(() => {
        if (type == 'tel') {
            setVal(toEnDigit(value?.toString()).replace(/[^0-9]/g, ''));
        } else if (commaInput) {
            setVal(addCommas(toFloat(value?.toString())));
        } else {
            setVal(value);
        }
    }, [value]);

    const parseEvent = function (e) {
        let val = e.target?.value?.toString() || '';
        if (type == 'tel') {
            val = toEnDigit(val).replace(/[^0-9]/g, '');
        } else if (commaInput) {
            val = toFloat(val);
        }
        e.target.value = val;

        return e;
    };

    return (
        <FormControl fullWidth error={{ error }.error != undefined} sx={{ ...theme.typography.customInput }}>
            <InputLabel htmlFor={id} sx={labelStyle}>
                {label}
            </InputLabel>
            <OutlinedInput id={id} type={type} name={name} value={val} onChange={(e) => onChange(parseEvent(e))} {...others} />
            {{ error } && (
                <FormHelperText error id={'standard-helper-' + id}>
                    {error}
                </FormHelperText>
            )}
        </FormControl>
    );
};

CustomInput.propTypes = {
    type: PropTypes.oneOf(['text', 'number', 'email', 'tel', 'password']),
    name: PropTypes.string,
    label: PropTypes.string,
    value: PropTypes.any,
    commaInput: PropTypes.bool,
    error: PropTypes.any,
    labelStyle: PropTypes.object
};

const CustomSelect = ({ name, label, value, error, children, ...others }) => {
    const theme = useTheme();
    const [id] = useState(uniqueId(`select-${name}-`));

    return (
        <FormControl fullWidth error={{ error }.error != undefined} sx={{ ...theme.typography.customInput }}>
            <InputLabel htmlFor={id}>{label}</InputLabel>
            <Select id={id} name={name} value={value} {...others}>
                {children}
            </Select>
            {{ error } && (
                <FormHelperText error id={'standard-helper-' + id}>
                    {error}
                </FormHelperText>
            )}
        </FormControl>
    );
};

CustomSelect.propTypes = {
    name: PropTypes.string,
    label: PropTypes.string,
    value: PropTypes.any,
    error: PropTypes.any,
    labelStyle: PropTypes.object,
    children: PropTypes.any
};

const CustomAutoComplete = ({ name, label, value, error, ...others }) => {
    const theme = useTheme();
    const [id] = useState(uniqueId(`autocomplete-${name}-`));

    return (
        <FormControl fullWidth error={{ error }.error != undefined} sx={{ ...theme.typography.customInput }}>
            <Autocomplete
                name={name}
                value={value}
                freeSolo
                onFocus={(e) => e.target?.select()}
                renderInput={(params) => {
                    return (
                        <FormControl fullWidth ref={params.InputProps.ref}>
                            <InputLabel {...params.InputLabelProps}>{label}</InputLabel>
                            <OutlinedInput inputProps={{ ...params.inputProps, className: '' }} />
                        </FormControl>
                    );
                }}
                {...others}
            />
            {{ error } && (
                <FormHelperText error id={'standard-helper-' + id}>
                    {error}
                </FormHelperText>
            )}
        </FormControl>
    );
};

CustomAutoComplete.propTypes = {
    name: PropTypes.string,
    label: PropTypes.string,
    value: PropTypes.any,
    error: PropTypes.any,
    labelStyle: PropTypes.object,
    children: PropTypes.any
};

export { CustomInput, CustomSelect, CustomAutoComplete, addCommas, toFloat };
